import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, auth, PageHead, errMsg } from './finance/shared';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';

const SOURCE_LABELS = { auto_match: 'مطابقة تلقائية برقم الجوال', invite_token: 'دعوة مباشرة' };

const LinkRequestsTab = ({ notify }) => {
  const [requests, setRequests] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/parent-link-requests`, auth());
      setRequests(res.data || []);
    } catch { setRequests([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await axios.put(`${API}/parent-link-requests/${id}/${action}`, {}, auth());
      notify(action === 'approve' ? 'تم قبول طلب الربط' : 'تم رفض طلب الربط');
      load();
    } catch (e) { notify(errMsg(e, 'فشل تنفيذ العملية')); }
  };

  if (requests === null) return <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>;
  if (requests.length === 0) return <EmptyState icon="inbox" text="لا توجد طلبات ربط بانتظار المراجعة" testId="parent-link-requests-empty" />;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="parent-link-requests-list">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-right text-xs text-slate-500">
          <tr>
            <th className="px-4 py-3">ولي الأمر</th>
            <th className="px-4 py-3">جوال ولي الأمر</th>
            <th className="px-4 py-3">اللاعب</th>
            <th className="px-4 py-3">المصدر</th>
            <th className="px-4 py-3">إجراء</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`parent-link-request-row-${r.id}`}>
              <td className="px-4 py-3 font-semibold text-slate-800">{r.parent_name}</td>
              <td dir="ltr" className="px-4 py-3 text-slate-600">{r.parent_phone}</td>
              <td className="px-4 py-3 text-slate-700">{r.player_name}</td>
              <td className="px-4 py-3 text-xs text-slate-500">{SOURCE_LABELS[r.source] || r.source}</td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  <button onClick={() => act(r.id, 'approve')} data-testid={`parent-link-approve-${r.id}`} className="flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-teal-700">
                    <Icon name="check" className="h-3.5 w-3.5" /> قبول
                  </button>
                  <button onClick={() => act(r.id, 'reject')} data-testid={`parent-link-reject-${r.id}`} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50">رفض</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RejectPrompt = ({ onConfirm, onCancel, title }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="reject-reason-modal">
      <div className="w-full max-w-sm rounded-xl bg-white p-6">
        <h4 className="font-bold text-slate-900">{title}</h4>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="سبب الرفض (اختياري)" className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-400 focus:outline-none" data-testid="reject-reason-input" />
        <div className="mt-4 flex gap-2">
          <button onClick={onCancel} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600">إلغاء</button>
          <button onClick={() => onConfirm(reason)} data-testid="reject-reason-confirm" className="flex-1 rounded-lg bg-red-600 py-2.5 text-sm font-bold text-white">تأكيد الرفض</button>
        </div>
      </div>
    </div>
  );
};

const EnrollmentRequestsTab = ({ notify }) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/enrollment-requests`, auth());
      setRequests(res.data || []);
    } catch { setRequests([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      const res = await axios.put(`${API}/enrollment-requests/${id}/approve`, {}, auth());
      notify('تم قبول الطلب — يفتح الآن ملف اللاعب لإكمال بياناته');
      const playerId = res.data?.player_id;
      if (playerId) navigate(`/players?highlight=${playerId}&edit=1`);
      else load();
    } catch (e) { notify(errMsg(e, 'فشل تنفيذ العملية')); }
  };

  const reject = async (reason) => {
    try {
      await axios.put(`${API}/enrollment-requests/${rejecting.id}/reject`, { reason }, auth());
      notify('تم رفض طلب التسجيل');
      setRejecting(null);
      load();
    } catch (e) { notify(errMsg(e, 'فشل تنفيذ العملية')); }
  };

  if (requests === null) return <div className="space-y-2">{[1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>;
  if (requests.length === 0) return <EmptyState icon="clipboard" text="لا توجد طلبات تسجيل جديدة بانتظار المراجعة" testId="enrollment-requests-empty" />;

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="enrollment-requests-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-right text-xs text-slate-500">
            <tr>
              <th className="px-4 py-3">الابن</th>
              <th className="px-4 py-3">البرنامج</th>
              <th className="px-4 py-3">ولي الأمر</th>
              <th className="px-4 py-3">جوال</th>
              <th className="px-4 py-3">ملاحظات</th>
              <th className="px-4 py-3">إجراء</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`enrollment-request-row-${r.id}`}>
                <td className="px-4 py-3 font-semibold text-slate-800">{r.child_full_name}</td>
                <td className="px-4 py-3 text-slate-600">{r.program_name}</td>
                <td className="px-4 py-3 text-slate-600">{r.parent_name}</td>
                <td dir="ltr" className="px-4 py-3 text-slate-600">{r.parent_phone}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.notes || '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => approve(r.id)} data-testid={`enrollment-approve-${r.id}`} className="flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-teal-700">
                      <Icon name="check" className="h-3.5 w-3.5" /> قبول
                    </button>
                    <button onClick={() => setRejecting(r)} data-testid={`enrollment-reject-${r.id}`} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50">رفض</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rejecting && <RejectPrompt title={`رفض طلب تسجيل «${rejecting.child_full_name}»`} onConfirm={reject} onCancel={() => setRejecting(null)} />}
    </>
  );
};

const ConfirmAmountPrompt = ({ receipt, onConfirm, onCancel }) => {
  const [amount, setAmount] = useState(receipt.amount ?? '');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="confirm-receipt-amount-modal">
      <div className="w-full max-w-sm rounded-xl bg-white p-6">
        <h4 className="font-bold text-slate-900">تأكيد إيصال «{receipt.player_name}»</h4>
        <p className="mt-1 text-xs text-slate-500">أكِّد المبلغ ليُضاف تلقائياً إلى المدفوع من اشتراك اللاعب. اتركه صفراً إن كنت تريد تأكيد الحالة فقط دون التأثير على الحساب المالي.</p>
        <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="confirm-receipt-amount-input" className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-teal-400 focus:outline-none" placeholder="المبلغ (ر.س)" />
        <div className="mt-4 flex gap-2">
          <button onClick={onCancel} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600">إلغاء</button>
          <button onClick={() => onConfirm(amount)} data-testid="confirm-receipt-amount-submit" className="flex-1 rounded-lg bg-teal-600 py-2.5 text-sm font-bold text-white">تأكيد وإضافة للحساب</button>
        </div>
      </div>
    </div>
  );
};

const ReceiptsTab = ({ notify }) => {
  const [receipts, setReceipts] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/parent-receipts`, auth());
      setReceipts(res.data || []);
    } catch { setReceipts([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirm = async (amount) => {
    try {
      const res = await axios.put(`${API}/parent-receipts/${confirming.id}/confirm`, { amount: amount ? Number(amount) : null }, auth());
      notify(res.data?.applied_to_finance ? 'تم تأكيد الإيصال وإضافة المبلغ لحساب اللاعب' : 'تم تأكيد الإيصال');
      setConfirming(null);
      load();
    } catch (e) { notify(errMsg(e, 'فشل تنفيذ العملية')); }
  };

  const reject = async (reason) => {
    try {
      await axios.put(`${API}/parent-receipts/${rejecting.id}/reject`, { reason }, auth());
      notify('تم رفض الإيصال');
      setRejecting(null);
      load();
    } catch (e) { notify(errMsg(e, 'فشل تنفيذ العملية')); }
  };

  const download = async (id, filename) => {
    try {
      const res = await axios.get(`${API}/parent-receipts/${id}/download`, { ...auth(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'receipt'; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { notify('تعذر تحميل الإيصال'); }
  };

  if (receipts === null) return <div className="space-y-2">{[1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>;
  if (receipts.length === 0) return <EmptyState icon="document" text="لا توجد إيصالات دفع بانتظار المراجعة" testId="parent-receipts-empty" />;

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="parent-receipts-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-right text-xs text-slate-500">
            <tr>
              <th className="px-4 py-3">اللاعب</th>
              <th className="px-4 py-3">المبلغ</th>
              <th className="px-4 py-3">ملاحظة</th>
              <th className="px-4 py-3">الملف</th>
              <th className="px-4 py-3">إجراء</th>
            </tr>
          </thead>
          <tbody>
            {receipts.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`parent-receipt-row-${r.id}`}>
                <td className="px-4 py-3 font-semibold text-slate-800">{r.player_name}</td>
                <td className="px-4 py-3 text-slate-600">{r.amount ? `${r.amount} ر.س` : '—'}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.note || '—'}</td>
                <td className="px-4 py-3">
                  <button onClick={() => download(r.id, r.filename)} data-testid={`parent-receipt-download-${r.id}`} className="flex items-center gap-1 text-xs font-bold text-teal-600 hover:text-teal-700">
                    <Icon name="download" className="h-3.5 w-3.5" /> {r.filename}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => setConfirming(r)} data-testid={`parent-receipt-confirm-${r.id}`} className="flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-teal-700">
                      <Icon name="check" className="h-3.5 w-3.5" /> تأكيد
                    </button>
                    <button onClick={() => setRejecting(r)} data-testid={`parent-receipt-reject-${r.id}`} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50">رفض</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rejecting && <RejectPrompt title={`رفض إيصال «${rejecting.player_name}»`} onConfirm={reject} onCancel={() => setRejecting(null)} />}
      {confirming && <ConfirmAmountPrompt receipt={confirming} onConfirm={confirm} onCancel={() => setConfirming(null)} />}
    </>
  );
};

const TABS = [
  { key: 'links', label: 'طلبات الربط' },
  { key: 'enrollments', label: 'طلبات التسجيل' },
  { key: 'receipts', label: 'إيصالات الدفع' },
];

export default function ParentLinkRequestsPage() {
  const [tab, setTab] = useState('links');
  const [toast, setToast] = useState('');
  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 3000); };

  return (
    <div className="space-y-4" data-testid="parent-link-requests-page">
      <PageHead title="بوابة ولي الأمر" desc="راجع طلبات ربط أولياء الأمور بأبنائهم، طلبات التسجيل الجديدة، وإيصالات الدفع المرفوعة" />

      {toast && <div className="rounded-lg bg-teal-50 px-4 py-2 text-sm text-teal-700" data-testid="parent-link-requests-toast">{toast}</div>}

      <div className="flex gap-1 rounded-xl bg-slate-100 p-1" data-testid="parent-portal-review-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`parent-portal-tab-${t.key}`} className={`flex-1 rounded-lg py-2 text-sm font-bold transition-all ${tab === t.key ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'links' && <LinkRequestsTab notify={notify} />}
      {tab === 'enrollments' && <EnrollmentRequestsTab notify={notify} />}
      {tab === 'receipts' && <ReceiptsTab notify={notify} />}
    </div>
  );
}
